import { AfterViewInit, Component, OnInit } from '@angular/core';
import { AgGridModule } from 'ag-grid-angular';
import {
  LittleBarsLoaderComponent,
  BarsLoaderComponent,
  formatDate,
  dateFilterParams,
  EntityCellRenderComponent,
  ActCellNameRendererComponent,
  ActCellIconType,
  UiStateService,
  PopupService,
  PopupType,
  ArchiveEmptyStateComponent,
} from '@upassignations/core';
import {
  ColDef,
  CellStyleModule,
  ClientSideRowModelModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  CustomFilterModule,
  PaginationModule,
  GridOptions,
  GridReadyEvent,
  ValidationModule,
  themeQuartz,
  RowStyleModule,
  ClientSideRowModelApiModule,
  GetDataPath,
  RowSelectionModule,
  RowSelectionOptions,
  RowNode,
  iconOverrides,
} from 'ag-grid-community';

import {
  SetFilterModule,
  ContextMenuModule,
  CellSelectionModule,
  TreeDataModule,
} from 'ag-grid-enterprise';

import { AG_GRID_LOCALE_FR } from '@ag-grid-community/locale';

import {
  FeatureService,
  AssignmentActionStatus,
  AssignmentStatus,
  LitigationServiceEndpoints,
} from '@upassignations/feature';
import { MetadataPanelComponent } from '../metadata-panel/metadata-panel.component';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, Observable, take } from 'rxjs';
import { ActionsHistoryComponent } from '../actions-history/actions-history.component';
import { SidePanel } from '../../models/current-panel';

@Component({
  selector: 'lib-archives',
  imports: [
    AgGridModule,
    CommonModule,
    MetadataPanelComponent,
    ActionsHistoryComponent,
  ],
  templateUrl: './archives.component.html',
  styleUrl: './archives.component.scss',
})
export class ArchivesComponent implements OnInit, AfterViewInit {
  private litigationSubject = new BehaviorSubject<any[]>([]);
  rowData$ = this.litigationSubject.asObservable();
  private isLoadingSubject = new BehaviorSubject<boolean>(true);
  isLoading$ = this.isLoadingSubject.asObservable();
  private metadataSubject = new BehaviorSubject<any[]>([]);
  metadata$ = this.metadataSubject.asObservable();
  private isDownloadingSubject = new BehaviorSubject<boolean>(false);
  isDownloading$ = this.isDownloadingSubject.asObservable();
  isDownloadComplete$ = new BehaviorSubject<boolean>(false);

  private actionsHistorySubject = new BehaviorSubject<any[]>([]);
  actionsHistory$ = this.actionsHistorySubject.asObservable();

  loadingMetadata = false;
  loadingActionsHistory = false;

  currentMetadata: any = null;
  currentActionsHistory: any = null;
  lastActionsHistoryUri: string | null = null;

  openSidePanel = false;
  gridApi: any;
  currentSidePanel = SidePanel.Metadata;

  SidePanel = SidePanel;

  modules = [
    ClientSideRowModelModule,
    ValidationModule,
    CellStyleModule,
    PaginationModule,
    TextFilterModule,
    NumberFilterModule,
    DateFilterModule,
    CustomFilterModule,
    SetFilterModule,
    ContextMenuModule,
    CellSelectionModule,
    RowStyleModule,
    ClientSideRowModelApiModule,
    RowSelectionModule,
    TreeDataModule,
  ];

  theme = themeQuartz
    .withParams({
      fontSize: 12,
      oddRowBackgroundColor: '#f8f8f8',
      rangeSelectionBorderColor: '#ff7900',
      rowHoverColor: '#ff790025',
      selectedRowBackgroundColor: '#ff790025',
      accentColor: '#ff7900',
    })
    .withPart(
      iconOverrides({
        type: 'image',
        mask: true,
        icons: {
          filter: {
            url: 'assets/icons/filter-icon.svg',
          },
          'menu-alt': {
            url: 'assets/icons/horizontal-bar-menu.svg',
          },
        },
      })
    );

  gridOptions: GridOptions = {
    pagination: true,
    paginationAutoPageSize: false,
    paginationPageSize: 11,
    paginationPageSizeSelector: [11, 20, 50, 100],
    loadingOverlayComponent: BarsLoaderComponent,
    allowContextMenuWithControlKey: false,
    getRowStyle: (params) => this.changeFontColorOnRow(params.data),
    noRowsOverlayComponent: ArchiveEmptyStateComponent,
  };

  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    flex: 1,
    wrapText: true,
    autoHeight: true,
    resizable: true,
    wrapHeaderText: true,
    cellStyle: {
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      lineHeight: '1.2',
      paddingTop: '2px',
      paddingBottom: '2px',
    },
    headerClass: 'ag-header',
  };
  autoGroupColumnDef: ColDef = {
    headerName: "Nom de l'acte",
    field: 'title',
    minWidth: 520,
    cellRenderer: 'agGroupCellRenderer',
    cellRendererParams: {
      innerRenderer: ActCellNameRendererComponent,
      innerRendererParams: this.getInnerRendererParams(),
      suppressCount: true,
    },
  };

  columnDefs: ColDef[] = [
    {
      headerName: 'Date de prise en charge',
      field: 'assignmentDate',
      filter: 'agDateColumnFilter',
      valueFormatter: this.dateFormatter,
      valueGetter: this.getDateValue('assignmentDate'),
      filterParams: dateFilterParams,
      minWidth: 180,
    },

    {
      headerName: 'Acte de procédure',
      field: 'proceduralAct',
      minWidth: 200,
    },
    {
      headerName: 'Statut',
      field: 'status',
      minWidth: 220,
      cellRenderer: (params) => this.statusCellRenderer(params.data),
    },
    {
      headerName: 'Réception',
      field: 'dateOfRecord',
      filter: 'agDateColumnFilter',
      valueFormatter: this.dateFormatter,
      valueGetter: this.getDateValue('dateOfRecord'),
      filterParams: dateFilterParams,
      minWidth: 150,
    },
    {
      headerName: 'Audience',
      field: 'hearingDateTime',
      filter: 'agDateColumnFilter',
      valueFormatter: this.dateFormatter,
      valueGetter: this.getDateValue('hearingDate'),
      filterParams: dateFilterParams,
      minWidth: 130,
    },

    {
      headerName: 'Juriste',
      field: 'assignedTo',
      minWidth: 180,
    },
    {
      headerName: 'Entité',
      field: 'responsibleEntity',
      minWidth: 250,
      cellRenderer: EntityCellRenderComponent,
      cellStyle: {
        display: 'flex',
        'align-items': 'center',
      },
    },
    {
      headerName: 'Date de création fiche litige',
      field: 'dateOfExport',
      filter: 'agDateColumnFilter',
      valueFormatter: this.dateFormatter,
      minWidth: 180,

      valueGetter: this.getDateValue('dateOfExport'),
      filterParams: dateFilterParams,
    },
    {
      headerName: 'Thème',
      field: 'topic',
      minWidth: 180,
    },
  ];

  rowSelection: RowSelectionOptions | 'single' | 'multiple' = {
    mode: 'singleRow',
    checkboxes: false,
    enableClickSelection: true,
  };
  selectedNodes: RowNode[] = []; // Array to store all selected nodes

  groupDefaultExpanded = 0;
  getDataPath: GetDataPath = (data) => data.path;

  localeText: Record<string, string> = AG_GRID_LOCALE_FR;

  constructor(
    // eslint-disable-next-line no-unused-vars
    private featureService: FeatureService,
    // eslint-disable-next-line no-unused-vars
    private uiStateService: UiStateService,
    // eslint-disable-next-line no-unused-vars
    private popupService: PopupService
  ) {}

  ngOnInit(): void {
    this.getLitigations();
  }
  ngAfterViewInit(): void {
    this.updateSidePanelDisplay();
  }

  updateSidePanelDisplay(): void {
    const trackingTable = document.querySelector(
      '.tracking-table'
    ) as HTMLElement;
    const sidePanelContainer = document.querySelector(
      '.tracking-table-side-panel'
    ) as HTMLElement;

    if (sidePanelContainer) {
      sidePanelContainer.style.visibility = 'hidden';
      sidePanelContainer.style.opacity = '0';
    }

    setTimeout(() => {
      if (trackingTable) {
        trackingTable.classList.add('initialized');
      }
      if (sidePanelContainer) {
        sidePanelContainer.style.visibility = 'visible';
        sidePanelContainer.style.opacity = '1';
      }
    }, 800);
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.handleLoadingOverlay(params);
    this.handleDataOverlay(params);
  }
  handleLoadingOverlay(params: GridReadyEvent): void {
    this.isLoading$.subscribe((isLoading) => {
      if (isLoading) {
        params.api.setGridOption('loading', true);
      } else if (!isLoading) {
        params.api.setGridOption('loading', false);
      }
    });
  }
  handleDataOverlay(params: GridReadyEvent): void {
    this.rowData$.subscribe((data) => {
      if (!this.isLoadingSubject.value) {
        const hasData = data && data.length > 0;
        console.log(data);

        if (hasData) {
          params.api.hideOverlay();
        } else {
          params.api.showNoRowsOverlay();
        }
      }
    });
  }

  getInnerRendererParams(): any {
    return {
      showMetadata: (uri: string, params: any) =>
        this.showMetadata(uri, params),
      showActionsHistory: (uri: string, params: any) =>
        this.showActionsHistory(uri, params),
      unarchiveLitigation: (uri: string, litigationRow: any) =>
        this.unarchiveLitigation(uri, litigationRow),
      isServiceDeactivated: (litigation: any, serviceEndpoint: string) =>
        this.isServiceDeactivated(litigation, serviceEndpoint),
      closeSidePanel: () => this.closeSidePanel(),
      manuallySelectRow: (node: RowNode) => this.manuallySelectRow(node),
      downloadLitigationDocument: (litigationUri) =>
        this.downloadLitigationDocument(litigationUri),
      displayingInArchives: true,
    };
  }

  optionMenuCellRenderer(): string {
    return `<img 
      src='assets/icons/option-menu.svg' 
      alt='icone de menu' 
      style="width: 20px; height:20px;"/>`;
  }
  statusCellRenderer(litigation: any): any {
    if (
      litigation.statusUri === AssignmentStatus.Assigned ||
      litigation.statusUri === AssignmentStatus.Exported
    ) {
      return this.emptyGreenCircle() + litigation.status;
    } else if (litigation.statusUri === AssignmentStatus.PendingAssignment) {
      return this.emptyOrangeCircle() + litigation.status;
    } else if (
      litigation.statusUri === AssignmentStatus.ProcessedWithError ||
      litigation.statusUri === AssignmentStatus.ExportedWithError ||
      litigation.statusUri === AssignmentStatus.NoResponsibleEntity
    ) {
      return this.emptyRedCircle() + litigation.status;
    } else if (litigation.statusUri === AssignmentStatus.NotToBeAssigned) {
      return this.emptyGrayCircle() + litigation.status;
    } else {
      return litigation.status;
    }
  }
  emptyGrayCircle(): string {
    return `<img 
      src='assets/icons/empty-gray-circle.svg' 
      alt='icone de cercle gris'
      style="width: 10px; height:10px;margin-right:10px"/>`;
  }
  emptyOrangeCircle(): string {
    return `<img 
      src='assets/icons/empty-orange-circle.svg' 
      alt='icone de cercle orange'
      style="width: 10px; height:10px;margin-right:10px"/>`;
  }
  emptyRedCircle(): string {
    return `<img 
      src='assets/icons/empty-red-circle.svg' 
      alt='icone de cercle rouge'
      style="width: 10px; height:10px;margin-right:10px"/>`;
  }
  emptyGreenCircle(): string {
    return `<img 
      src='assets/icons/empty-green-circle.svg' 
      alt='icone de cercle vert'
      style="width: 10px; height:10px;margin-right:10px"/>`;
  }
  changeFontColorOnRow(litigation: any): any {
    if (
      litigation?.statusUri === AssignmentStatus.Deprecated ||
      litigation?.statusUri === AssignmentStatus.Duplicated
    ) {
      return { color: '#c2c3c4' };
    }
    return {};
  }

  getDateValue(fieldName: string): any {
    return (params: any) => {
      return params.data?.[fieldName] ? new Date(params.data[fieldName]) : null;
    };
  }
  dateFormatter(params: { value: any }): string {
    return formatDate(params.value);
  }

  transformLitigations(response: any): any[] {
    const litigationsArray = response.litigations || [];

    return litigationsArray.map((litigation) => ({
      ...litigation,
      hearingDateTime: litigation.hearingDate
        ? litigation.hearingTime
          ? `${litigation.hearingDate}T${litigation.hearingTime}`
          : litigation.hearingDate
        : null,
    }));
  }

  getLitigations(): any {
    this.featureService.getArchivedLitigation$().subscribe((litigations) => {
      const transformedLitigations = this.transformLitigations(litigations);
      this.litigationSubject.next(transformedLitigations);
      this.isLoadingSubject.next(false);
    });
  }

  resetFilters(): void {
    if (this.gridApi) {
      this.gridApi.setFilterModel(null);
    }
  }
  handleExcelExport(): void {
    if (this.gridApi) {
      const urisToExport = this.getURIsToExport();
      this.exportToExcel(urisToExport);
    }
  }
  getURIsToExport(): string[] {
    const filteredUris: string[] = [];
    this.gridApi.forEachNodeAfterFilter((node) => {
      if (node.data?.uri) {
        filteredUris.push(node.data.uri);
      }
    });
    return filteredUris;
  }
  exportToExcel(urisToExport: string[]): void {
    this.isDownloadingSubject.next(true);
    this.isDownloadComplete$.next(false);

    this.featureService.exportLitigation$(urisToExport).subscribe({
      next: (response) => {
        if (!response.error) {
          this.downloadGeneratedFile(response.fileName);
        }
      },
      error: () => {
        this.isDownloadingSubject.next(false);
        this.isDownloadComplete$.next(false);
      },
      complete: () => {
        this.isDownloadingSubject.next(false);
        this.isDownloadComplete$.next(true);

        setTimeout(() => {
          this.isDownloadComplete$.next(false);
        }, 1000);
      },
    });
  }

  downloadLitigationDocument(litigationUri: string): void {
    this.isDownloadingSubject.next(true);
    this.isDownloadComplete$.next(false);
    this.featureService.downloadLitigationDocument$(litigationUri).subscribe({
      next: (response) => {
        if (!response.error) {
          this.downloadGeneratedFile(response.fileName);
        }
      },
      error: () => {
        this.isDownloadingSubject.next(false);
        this.isDownloadComplete$.next(false);
      },
      complete: () => {
        this.isDownloadingSubject.next(false);
        this.isDownloadComplete$.next(true);

        setTimeout(() => {
          this.isDownloadComplete$.next(false);
        }, 1000);
      },
    });
  }

  downloadGeneratedFile(fileName: string): void {
    if (fileName) {
      this.featureService
        .downloadExportedFileFromServer(fileName)
        .subscribe((response) => {
          this.convertFileStreamToLocalFile(response, fileName);
        });
    }
  }
  convertFileStreamToLocalFile(fileStream: BlobPart, fileName: string): void {
    const blob = new Blob([fileStream], { type: 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
  }

  isServiceDeactivated(litigation: any, serviceEndpoint: string): boolean {
    if (!this.litigationContainsDeactivatedServices(litigation)) {
      return false;
    }

    const serviceToCheck = `litigationServices:_${serviceEndpoint}`;

    const isDeactivated =
      litigation.deactivatedServices.includes(serviceToCheck);

    return isDeactivated;
  }
  litigationContainsDeactivatedServices(litigation: any): boolean {
    return litigation?.deactivatedServices;
  }

  executeServiceIfEnabled(
    litigation: any,
    serviceName: string,
    action: () => void
  ): void {
    if (!this.isServiceDeactivated(litigation, serviceName)) {
      action();
    }
  }

  // management to assignation

  getAssignmentStatus(litigation: any): string {
    const assignmentIsDisabled = litigation.isDeprecated;

    const isAssigned = this.isServiceDeactivated(
      litigation,
      LitigationServiceEndpoints.ASSIGN_LITIGATION
    );

    const isDeassigned = this.isServiceDeactivated(
      litigation,
      LitigationServiceEndpoints.DEASSIGN_LITIGATION
    );

    const noActionOnAssignment = isAssigned && isDeassigned;

    if (assignmentIsDisabled) {
      return AssignmentActionStatus.Deactivated;
    } else if (noActionOnAssignment) {
      return AssignmentActionStatus.NoAssignmentAction;
    } else if (isAssigned) {
      return AssignmentActionStatus.Assigned;
    } else if (isDeassigned) {
      return AssignmentActionStatus.Deassigned;
    }
    return '';
  }

  // Metadata:

  subscribeToMetadata$(uri: string): any {
    this.openSidePanel = true;
    this.featureService.getLitigationMetadata$(uri).subscribe({
      next: (metadatas) => {
        const formattedMetadata = metadatas.map(({ key, value }) => ({
          key,
          value: this.formatMetadataValue(key, value),
        }));

        this.metadataSubject.next(formattedMetadata);
        this.currentMetadata = formattedMetadata;
        this.updateSidePanelLoadingStatus(SidePanel.Metadata, false);
      },
    });
  }

  subscribeToActionsHistory$(uri: string): any {
    this.openSidePanel = true;
    this.featureService.getActionsHistory$(uri).subscribe({
      next: (actionsHistory) => {
        const formattedActionsHistory = actionsHistory.map(
          ({ date, action, auteur }) => ({
            date: formatDate(date),
            action,
            auteur,
          })
        );

        this.actionsHistorySubject.next(formattedActionsHistory);
        this.currentActionsHistory = formattedActionsHistory;
        this.updateSidePanelLoadingStatus(SidePanel.ActionsHistory, false);
      },
    });
  }

  updateSidePanelLoadingStatus(sidePanel: SidePanel, status: boolean): void {
    if (sidePanel === SidePanel.Metadata) {
      this.loadingMetadata = status;
    } else {
      this.loadingActionsHistory = status;
    }
  }

  formatMetadataValue(key: string, value: any): any {
    if (key.toLowerCase().includes('date') && typeof value === 'string') {
      return formatDate(value);
    }
    if (value === true) return 'oui';
    if (value === false) return 'non';
    if (value === null) return '';
    return value;
  }

  showMetadata(uri: string, params?: any): void {
    this.updateCurrentSidePanel(SidePanel.Metadata);
    this.updateSidePanelLoadingStatus(SidePanel.Metadata, true);
    this.subscribeToMetadata$(uri);

    if (!params.node.isSelected()) {
      this.manuallySelectRow(params.node);
    }
    this.updateCurrentSidePanel(SidePanel.Metadata);
  }

  showActionsHistory(uri: string, params: any): void {
    this.updateCurrentSidePanel(SidePanel.ActionsHistory);
    this.updateSidePanelLoadingStatus(SidePanel.ActionsHistory, true);
    this.subscribeToActionsHistory$(uri);

    if (!params.node.isSelected()) {
      this.manuallySelectRow(params.node);
    }
  }

  unarchiveLitigation(litigationUri: string, litigationRow: any): void {
    this.confirmAction(
      `Voulez-vous désarchiver l'acte : ${litigationRow.title}?`
    ).subscribe((confirmed) => {
      if (confirmed) {
        this.featureService.unarchiveLitigation$(litigationUri).subscribe({
          next: (response) => {
            if (!response.error) {
              this.popupService.trigger(
                PopupType.Success,
                "L'acte a bien été désarchivé",
                []
              );
              this.gridApi.applyTransaction({ remove: [litigationRow] });
            }
          },
        });
      }
    });
  }

  confirmAction(confirmationQuestion: string): Observable<boolean> {
    const actions = [
      {
        text: 'Confirmer',
        value: true,
      },
    ];
    const choice = this.popupService
      .trigger(PopupType.Alert, confirmationQuestion, actions)
      .pipe(take(1));
    return choice;
  }
  manuallySelectRow(node: RowNode): void {
    this.selectedNodes = [node];
    node.setSelected(true);
  }

  updateCurrentSidePanel(sidePanel: SidePanel): void {
    this.currentSidePanel = sidePanel;
  }

  closeSidePanel(): void {
    this.uiStateService.clearActiveIcons();
    this.openSidePanel = false;
    setTimeout(() => {
      this.currentMetadata = null;
    }, 1200);
  }

  onRowClicked(event: any): void {
    const selectedNode: RowNode = event.node;
    this.selectedNodes = [selectedNode];
    const uri = selectedNode.data.uri;
    const rowId = `row_${uri}`;
    this.uiStateService.setSelectedRowId(rowId);

    if (this.openSidePanel) {
      const cellId = `cell_${uri}`;

      if (this.currentSidePanel === SidePanel.Metadata) {
        if (this.uiStateService.shouldDisplayMetadata(uri)) {
          this.updateSidePanelLoadingStatus(SidePanel.Metadata, true);
          this.subscribeToMetadata$(uri);
          this.uiStateService.setLastDisplayedMetadataUri(uri);
        }
        this.uiStateService.setActiveIcon(cellId, ActCellIconType.Info);
      } else {
        if (this.lastActionsHistoryUri !== uri) {
          this.updateSidePanelLoadingStatus(SidePanel.ActionsHistory, true);
          this.subscribeToActionsHistory$(uri);
          this.lastActionsHistoryUri = uri;
        }
      }
    }
  }
}
